import type { NodePath } from '@fullstory/renderer-shared';
import { SimulatedPseudoClass } from '@fullstory/rewriters';
import type { CursorIcon } from '../frame_playback_host';
import type { FsDocument } from './fsdocument';
import { FsDocumentNode } from './fsdocumentorshadowroot';
import type { FsElement, FsNode } from './fsnode';

/**
 * PseudoClassManager transforms FsNodes to simulate pseudo-classes that would
 * normally be applied natively by the browser.
 */
export class PseudoClassManager {
  private _focused: FsElement | null = null;
  private _hovered: FsElement | null = null;

  constructor(private rootDoc: FsDocument) {}

  /**
   * Simulate :focus for the given node, and simulate :focus-within for the
   * given node and all its ancestors.
   */
  focus(node: FsNode): void {
    if (node === this._focused) {
      return;
    }

    if (!node) {
      console.warn('focus() called without a node to focus');
      return;
    }

    if (node instanceof FsDocumentNode) {
      console.warn('focus() called on a document node');
      return;
    }

    const focusedElement = this._changePathWithPseudoClass(
      SimulatedPseudoClass.FocusWithin,
      this._focused,
      node,
      n => n.parent,
    );
    if (!focusedElement) {
      return;
    }

    if (this._focused) {
      this.disablePseudoClass(SimulatedPseudoClass.Focus, this._focused);
    }
    this.enablePseudoClass(SimulatedPseudoClass.Focus, focusedElement);

    this._focused = focusedElement;
  }

  /**
   * Stop simulating :focus for the given node. (Or stop simulating it for the
   * currently focused node, if no node is provided.)
   */
  blur(node?: FsNode | undefined): void {
    const focused = this._focused;
    this._focused = null;

    if (focused) {
      this._blurNode(focused);
    }

    if (!node || node === focused) {
      return;
    }

    console.warn(`Blurring node ${node}, but node ${focused} is focused`);
    this._blurNode(node);
  }

  private _blurNode(node: FsNode): void {
    if (!node.isElement()) {
      return;
    }

    if (this.hasPseudoClass(SimulatedPseudoClass.Focus, node)) {
      this.disablePseudoClass(SimulatedPseudoClass.Focus, node);
    }

    if (this.hasPseudoClass(SimulatedPseudoClass.FocusWithin, node)) {
      this._changePathWithPseudoClass(SimulatedPseudoClass.FocusWithin, node, null, n => n.parent);
    }
  }

  /**
   * Simulate :hover for the given node and all its ancestors. This
   * automatically stops simulating :hover for nodes that should no longer be
   * hovered after this change.
   * @returns the mouse cursor type that should be displayed when hovering over
   * the given node, or undefined if it could not be determined.
   */
  hover(node: FsNode): CursorIcon | undefined {
    if (!node) {
      console.warn('hover() called without a node to hover');
      return undefined;
    }

    if (node instanceof FsDocumentNode) {
      console.warn('hover() called on a document node');
      return undefined;
    }

    const hoveredElement = this._changePathWithPseudoClass(
      SimulatedPseudoClass.Hover,
      this._hovered,
      node,
      n => this._getParentOrFrame(n),
    );
    if (!hoveredElement || !hoveredElement.isAttached()) {
      this._hovered = null;
      return undefined;
    }
    this._hovered = hoveredElement;

    // We want to set the cursor style, but if element is not flushed, or if we
    // don't have a window object, we won't be able to get the style information
    // we need to do it.
    if (!this._hovered.dom) {
      return undefined;
    }
    const wnd = this._hovered.ownerDoc.frame?.contentWindow;
    if (!wnd) {
      return undefined;
    }

    // Apply cursor: styles.
    // TODO(jgw): Decide whether we want to faithfully reproduce other cursor
    // types (e.g., text i-beam).
    const computedStyle = wnd.getComputedStyle(this._hovered.dom);
    const cssCursor =
      computedStyle.getPropertyValue('--fs-cursor') || computedStyle.getPropertyValue('cursor');
    switch (cssCursor) {
      case 'pointer':
        return 'cursor-pointer';

      default:
        // Slightly weird: The browser won't tell you what it thinks "auto"
        // should do, so you have to guess.
        switch (this._hovered.tag) {
          case 'a':
            return 'cursor-pointer';

          default:
            return 'cursor';
        }
    }
  }

  private _getParentOrFrame(node: FsNode | null): FsNode | null | undefined {
    if (!node) {
      return undefined;
    }
    // Special case for the root element, since _changePathWithPseudoClass
    // will halt iteration at that point.
    if (node.parent && !(node.parent instanceof FsDocumentNode)) {
      return node.parent;
    }
    if (!this.rootDoc) {
      console.error(`Tried to set pseudoclasses without a root doc.`);
      return undefined;
    }
    // If we're already at the root document, return `node`'s parent,
    // even if it's the document node, and even if there isn't one.
    const { ownerDoc } = node;
    if (ownerDoc === this.rootDoc) {
      return node.parent;
    }
    return ownerDoc?.frame?._fs;
  }

  /**
   * Enable `pseudoClass` on `newPathEnd` and all of its ancestors.
   * Disable `pseudoClass` on `oldPathEnd` and all of its ancestors.
   * This is used to simulated :hover and :focus-within, which must be simulated
   * on all ancestors and not just the directly hovered / focused node.
   * @returns the nearest element ancestor of `newPathEnd`. This is the newly
   * hovered or focused element. It may differ from `newPathEnd` if `newPathEnd`
   * is a text node or some other variety of non-element node.
   */
  private _changePathWithPseudoClass(
    pseudoClass: SimulatedPseudoClass,
    oldPathEnd: FsNode | null,
    newPathEnd: FsNode | null,
    getParent: (n: FsNode) => FsNode | undefined | null,
  ): FsElement | undefined {
    // Walk up the document from the end of the new path, and mark nodes that
    // didn't have pseudoClass.
    let commonAncestor: FsElement | undefined;
    let nearestElementAncestor: FsElement | undefined;
    let currentNode = newPathEnd;
    if (currentNode) {
      const seen = new WeakMap<FsNode, boolean>();
      do {
        // protect against infinite cycles
        if (seen.has(currentNode)) {
          break;
        }
        seen.set(currentNode, true);

        if (currentNode.isElement()) {
          if (!nearestElementAncestor) {
            nearestElementAncestor = currentNode;
          }
          if (this.hasPseudoClass(pseudoClass, currentNode)) {
            commonAncestor = currentNode;
            break;
          }
          this.enablePseudoClass(pseudoClass, currentNode);
        }
        currentNode = getParent(currentNode) ?? null;
      } while (currentNode && !(currentNode instanceof FsDocumentNode));

      // If we didn't find an element ancestor but expected to, keep the previous
      // state. You can't hover e.g. a Document node.
      if (!nearestElementAncestor) {
        return undefined;
      }
    }

    // Walk up from oldPathEnd and unmark nodes until we reach the first node
    // that's an ancestor of both the new and previous nodes with pseudoClass OR
    // the document root. Note that oldPathEnd may be null!
    currentNode = oldPathEnd;
    const seen = new WeakMap<FsNode, boolean>();
    while (
      currentNode &&
      currentNode != commonAncestor &&
      !(currentNode instanceof FsDocumentNode)
    ) {
      // protect against infinite cycles
      if (seen.has(currentNode)) {
        break;
      }
      seen.set(currentNode, true);

      if (currentNode.isElement()) {
        this.disablePseudoClass(pseudoClass, currentNode);
      }
      currentNode = getParent(currentNode) ?? null;
    }

    // Note that if `newPathEnd` is not an Element, we return the nearest
    // Element instead. We have no way of simulating pseudoclasses on e.g.
    // a Text node, and there's no way of applying a style to Text nodes
    // directly anyway.
    return nearestElementAncestor;
  }

  // These DOM manipulation functions are visible as methods for testing.
  hasPseudoClass(kind: SimulatedPseudoClass, elem: FsElement): boolean {
    return elem.hasAttr(kind);
  }

  enablePseudoClass(kind: SimulatedPseudoClass, elem: FsElement): void {
    elem.setAttr(kind, '', /* skipRewrite = */ true);
  }

  disablePseudoClass(kind: SimulatedPseudoClass, elem: FsElement): void {
    elem.setAttr(kind, null, /* skipRewrite = */ true);
  }

  // Provide convenience methods for the NodePath type.
  hoverPath(path: NodePath): void {
    if (this._hovered?.path === path) {
      return;
    }
    const node = this.rootDoc.getRelatedNode(path);
    if (!node) {
      return;
    }
    this.hover(node);
  }

  focusPath(path: NodePath): void {
    if (this._focused?.path === path) {
      return;
    }
    const node = this.rootDoc.getRelatedNode(path);
    if (!node) {
      return;
    }
    this.focus(node);
  }
}
